import {listEdits,readEdit,replaceEditIfUnchanged,editResourceKey,recoveryEdits} from "./draftStore";
import {offlineIdentity,activeDrafts} from "./cache";
import {RevisionDraft,type EditRecord} from "./revisionDraft";
import {ApiError,readChapter,getBookPlan,listVolumes,saveChapter,saveBookPlan,updateVolume,getWork,updateWork} from "../api/client";
import {workInfo,type WorkInfo} from "../../features/writing/workInfoModel";
import type {ChapterValue} from "../../features/writing/useChapterEditor";
/** Sync only this authenticated author's inactive chapter/plan records. Active editors own their own queues. */
const running=new Map<string,Promise<{synchronized:number;conflicts:number}>>();
export function syncSavedDrafts(userId:string){
 const active=running.get(userId);if(active)return active;
 const next=sync(userId).finally(()=>{if(running.get(userId)===next)running.delete(userId);});
 running.set(userId,next);return next;
}
async function sync(userId:string){
 const items=await listEdits(userId);let synchronized=0,conflicts=0;
 for(const item of items){
  const identity=offlineIdentity();if(!identity||identity.offline||identity.id!==userId)break;
  const resourceKey=editResourceKey(item.key);
  if(activeDrafts.has(resourceKey))continue;
  const variants=await recoveryEdits(resourceKey);
  if(variants.length>1||variants.find(row=>row.key===item.key)?.heldForReview)continue;
  const [ownerId,workId,kind,id]=resourceKey.split(":");if(ownerId!==userId||!workId||!["chapter","volume","book-plan","work-info"].includes(kind??""))continue;
  const record=await readEdit<EditRecord<unknown>>(item.key);if(!record||record.conflicted){if(record?.conflicted)conflicts++;continue;}
  const canWrite=()=>!activeDrafts.has(resourceKey)&&offlineIdentity()?.id===userId&&!offlineIdentity()?.offline;
  let expected:EditRecord<unknown>|null=record;
  try{
   let remote:unknown,revision:number;
   if(kind==="chapter"&&id){const r=await readChapter(workId,id);remote={title:r.chapter.title,doc:r.chapter.doc,plan:r.chapter.plan};revision=r.chapter.revision;}
   else if(kind==="book-plan"){const r=await getBookPlan(workId);remote=r.plan.data;revision=r.plan.revision;}
   else if(kind==="work-info"){const r=await getWork(workId);remote=workInfo(r.work);revision=r.work.revision;}
   else{const r=(await listVolumes(workId)).items.find(v=>v.id===id);if(!r)continue;remote={title:r.title,direction:r.direction};revision=r.revision;}
   if(!canWrite())continue;
   const draft=new RevisionDraft({value:remote,revision,isConflict:e=>e instanceof ApiError&&e.status===409,
    isRejected:e=>e instanceof ApiError&&[400,413,422].includes(e.status),
    persist:async r=>{
     if(!await replaceEditIfUnchanged(item.key,expected,r,canWrite,true))throw new Error("本机稿件已更新或已打开，后台同步已让出保存权。");
     expected=r;
    },
    send:async attempt=>{
     if(!canWrite())throw new Error("此草稿已在编辑器打开或账号已切换");
     if(kind==="chapter")return saveChapter(workId,id!,{mutationId:attempt.mutationId,expectedRevision:attempt.expectedRevision,deviceId:"offline-sync",...attempt.value as ChapterValue});
     if(kind==="book-plan")return {revision:(await saveBookPlan(workId,{mutationId:attempt.mutationId,expectedRevision:attempt.expectedRevision,data:attempt.value as import("@novel/contracts").BookPlanData})).plan.revision};
     if(kind==="work-info")return {revision:(await updateWork(workId,{mutationId:attempt.mutationId,expectedRevision:attempt.expectedRevision,...attempt.value as WorkInfo})).work.revision};
     const value=attempt.value as {title:string;direction:Record<string,unknown>};return {revision:(await updateVolume(workId,id!,{mutationId:attempt.mutationId,expectedRevision:attempt.expectedRevision,...value})).volume.revision};
    }});
   draft.restore(record);if(await draft.flush())synchronized++;else if(draft.getSnapshot().status==="conflict")conflicts++;
  }catch(e){if(e instanceof ApiError&&(e.status===404||e.status===409)){
   if(await replaceEditIfUnchanged(item.key,expected,{...record,conflicted:true},canWrite,true))conflicts++;
  }}
 }
 return {synchronized,conflicts};
}
